"use client";

import { useQuery } from "@tanstack/react-query";
import {
  NotificationPreference,
  getComedianNotificationPreference,
} from "@/data/notification-preferences";
import { Database } from "@punchup/data/supabase.types";
import { createClientComponentClient } from "@punchup/data/supabase/client";

/**
 * Query key factory for comedian notification preferences
 */
export const comedianNotificationPreferenceKeys = {
  all: ["comedian-notification-preference"] as const,
  detail: (comedianId: string) =>
    ["comedian-notification-preference", comedianId] as const,
};

/**
 * Hook to fetch a single comedian's notification preference
 *
 * @param comedianId - The comedian ID to fetch preference for
 * @returns Query result with preference data
 *
 * @example
 * ```tsx
 * const { data: preference, isLoading } = useComedianNotificationPreference(comedianId);
 * ```
 */
export function useComedianNotificationPreference(comedianId: string) {
  const supabase = createClientComponentClient<Database>();

  return useQuery<NotificationPreference>({
    queryKey: comedianNotificationPreferenceKeys.detail(comedianId),
    queryFn: () => getComedianNotificationPreference(supabase, comedianId),
    enabled: !!comedianId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}
